import DataTable from "../components/common/DataTable";
import { Button, Text } from "@mantine/core";
import React from "react";
import { CSVLink } from "react-csv";
import Head from "../components/common/Head";
import useValidate from "../hooks/useValidate";
import { getAllNonActiveSubs } from "../utils/api";
import useStore from "../utils/store";

const AllNonActiveSubs = () => {
  const user = useStore((state) => state.user);
  
  const [subscriptions, setSubscriptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [totalData, setTotalData] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);

  const getAllNonActiveSubsData = async (page = 1, size = 20) => {
    if (!user?.getAllSubscriptions) {
      return;
    }
    setLoading(true);
    const [success, response] = await getAllNonActiveSubs(page, size);
    if (success) {
      setSubscriptions(response.data || []);
      setTotalData(response.metadata?.totalData || 0);
      setTotalPages(response.metadata?.totalPages || 0);
      setCurrentPage(response.metadata?.currentPage || 1);
    }
    setLoading(false);
  };

  const handlePageChange = (page, size) => {
    const nextSize = size ?? pageSize;
    setCurrentPage(page);
    setPageSize(nextSize);
    getAllNonActiveSubsData(page, nextSize);
  };

  useValidate();
  React.useEffect(() => {
    getAllNonActiveSubsData(currentPage, pageSize);
  }, []);

  if (!user?.getAllSubscriptions) {
    return (
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <Text c="dimmed">
          You don't have permission to view subscriptions
        </Text>
      </div>
    );
  }

  return (
    <>
      
      <Head
        title="Non-Active Subscriptions"
        customButton={
          user?.getAllSubscriptions && (
            <CSVLink
              target="_blank"
              data={
                subscriptions?.map((item) => ({
                  name: item?.name,
                  email: item?.email,
                  phoneNumber: item?.phoneNumber,
                  subscriptionType: item?.subscriptionType?.title,
                  subscriptionBuyDate: item?.subscriptionBuyDate
                    ? new Date(item?.subscriptionBuyDate).toLocaleDateString()
                    : "-",
                  subscriptionExpireDate: item?.subscriptionExpireDate
                    ? new Date(
                        item?.subscriptionExpireDate
                      ).toLocaleDateString()
                    : "-",
                  status: item?.status || "Expired",
                })) || []
              }
              filename={`non_active_subscriptions${
                new Date().toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                }) +
                new Date().toLocaleTimeString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              }.csv`}
            >
              <Button>Download CSV</Button>
            </CSVLink>
          )
        }
      />

      <DataTable
        loading={loading}
        dataSource={subscriptions}
        scroll={{ x: 1000, y: 600 }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalData,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: [20, 100, 500, 1000, 5000, 10000, 20000],
          onChange: handlePageChange,
          onShowSizeChange: handlePageChange,
        }}
        columns={[
          {
            title: "Name",
            dataIndex: "name",
          },
          {
            title: "Email",
            dataIndex: "email",
          },
          {
            title: "Phone Number",
            dataIndex: "phoneNumber",
          },
          {
            title: "Subscription Name",
            dataIndex: ["subscriptionType", "title"],
          },
          {
            title: "Subscription Buy Date",
            dataIndex: "subscriptionBuyDate",
            render: (text) => {
              return text
                ? new Date(text).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "-";
            },
          },
          {
            title: "Subscription Expire Date",
            dataIndex: "subscriptionExpireDate",
            render: (text) => {
              return text
                ? new Date(text).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "-";
            },
          },
          {
            title: "Status",
            dataIndex: "status",
            render: (text) => {
              const status = text || "Expired";
              return (
                <span
                  style={{
                    color: status === "Expired" ? "#ff4d4f" : "#faad14",
                    fontWeight: "bold",
                  }}
                >
                  {status}
                </span>
              );
            },
          },
        ]}
      />
    </>
  );
};

export default AllNonActiveSubs;
